// Fila de un viaje que espera accion, en el Inicio (PP-64, rediseño): una
// oferta para responder (Aceptar / Rechazar con motivo) o un aceptado para
// asignar (AssignForm de la bandeja). Misma logica que la bandeja por planta:
// tripActions decide que botones se habilitan y el reloj se descuenta contra
// el `deadlineAt` del servidor. La UI deshabilita, la API decide.
//
// La forma es la de la sala de control: la hora de salida grande a la
// izquierda, que viaje y en que planta en el medio, el plazo como el
// numero mas grande de la fila a la derecha (naranja con menos de 5 minutos,
// critico en los ultimos 15 segundos) y las acciones al final. El lector de
// pantalla recibe el plazo como frase completa.
import { useId, useState } from "react";
import type { Driver, OfferRejectReason, ProviderMultiSiteTrip, SiteVehicle } from "@ucorp/contracts";
import { Button } from "../components/ui/button.js";
import { cn } from "../components/ui/cn.js";
import { SelectField } from "../fleet/parts.js";
import type { RosterState } from "../fleet/useRoster.js";
import { asProviderTrip } from "../logic/home.js";
import { formatClock, formatDate, formatRemaining, remainingSeconds, summaryParts, tripActions, tripTitle } from "../logic/requests.js";
import { AssignForm } from "../requests/AssignForm.js";
import { REJECT_REASON_LABEL, REJECT_REASONS } from "../requests/labels.js";

interface Props {
  row: ProviderMultiSiteTrip;
  now: number;
  /** Zona IANA de la planta de la fila. */
  timezone: string | undefined;
  /** Dia operativo de hoy en la planta: una oferta de otro dia lo dice. */
  today: string | undefined;
  busy: boolean;
  vehicles: RosterState<SiteVehicle>;
  drivers: RosterState<Driver>;
  /** Ultimo conductor de cada vehiculo en la planta de la fila (PP-65), para proponerlo al asignar. */
  lastDrivers?: ReadonlyMap<string, string>;
  onAccept: (row: ProviderMultiSiteTrip, offerId: number) => Promise<boolean>;
  onReject: (row: ProviderMultiSiteTrip, offerId: number, reason: OfferRejectReason) => Promise<boolean>;
  onAssign: (row: ProviderMultiSiteTrip, vehicleId: string, driverId: string) => Promise<boolean>;
}

/** Menos de 5 minutos es urgente (naranja); los ultimos 15 segundos, critico. */
const URGENT_SECONDS = 300;
const CRITICAL_SECONDS = 15;

function Deadline({ row, now }: { row: ProviderMultiSiteTrip; now: number }): React.JSX.Element | null {
  if (row.offer === undefined) return null;
  const seconds = remainingSeconds(row.offer.deadlineAt, now);
  const what = row.offer.state === "offered" ? "responder" : "asignar";
  if (seconds === 0) {
    return (
      <p className="m-0 text-xs font-medium leading-tight text-critico-texto md:text-right">
        Tiempo cumplido para {what}. Consultando el estado...
      </p>
    );
  }
  const tone = seconds <= CRITICAL_SECONDS ? "text-critico-texto" : seconds <= URGENT_SECONDS ? "text-naranja-texto" : "text-texto";
  return (
    <p className="m-0 leading-tight md:text-right">
      <span className="sr-only">
        Quedan {formatRemaining(seconds)} para {what}.
      </span>
      <span aria-hidden="true" className="flex items-baseline gap-1.5 md:flex-col md:items-end md:gap-0">
        <b className={cn("tabular text-base font-semibold", tone)}>{formatRemaining(seconds)}</b>
        <span className={cn("text-eyebrow", seconds <= URGENT_SECONDS ? tone : "text-texto-suave")}>para {what}</span>
      </span>
    </p>
  );
}

export function PendingRow({ row, now, timezone, today, busy, vehicles, drivers, lastDrivers, onAccept, onReject, onAssign }: Props): React.JSX.Element {
  const titleId = useId();
  const [panel, setPanel] = useState<"none" | "reject" | "assign">("none");
  const [reason, setReason] = useState<OfferRejectReason | "">("");
  const trip = asProviderTrip(row);
  const actions = tripActions(trip, now);
  const offerId = row.offer?.offerId;
  // Sin zona de la planta no se pinta ninguna hora: nunca la del navegador ni UTC.
  const summary = summaryParts(row.summary, timezone ?? "");
  const departure = row.summary?.departureAt === undefined || timezone === undefined ? "" : formatClock(row.summary.departureAt, timezone);
  // Un panel que la API ya no admite (vencio, lo tomo otro operador) se cierra solo.
  const open = (panel === "reject" && !actions.respond) || (panel === "assign" && !actions.assign) ? "none" : panel;

  return (
    <li
      aria-labelledby={titleId}
      className="grid grid-cols-[56px_minmax(0,1fr)] items-center gap-x-3 gap-y-2 border-t border-divisor px-3.5 py-2.5 first:border-t-0 hover:bg-velo md:grid-cols-[64px_minmax(0,1fr)_auto_auto] md:gap-x-3.5"
    >
      <p className="tabular m-0 self-start text-base font-semibold leading-tight text-texto">{departure === "" ? "--:--" : departure}</p>
      <div className="min-w-0">
        <h3 id={titleId} className="tabular m-0 text-sm font-medium leading-tight text-texto">
          {tripTitle(trip, timezone)}
        </h3>
        {today !== undefined && row.date !== today && (
          <p className="m-0 text-xs font-semibold text-ambar">Viaje del {formatDate(row.date)}</p>
        )}
        <p aria-label="Resumen del viaje" className="tabular m-0 mt-0.5 truncate text-xs text-texto-suave">
          <span>{row.siteName}</span>
          {summary.length > 0 && ` · ${summary.join(" · ")}`}
        </p>
      </div>
      <div className="col-start-2 md:col-start-auto md:min-w-[88px]">
        <Deadline row={row} now={now} />
      </div>
      <div className="col-start-2 flex flex-wrap gap-1.5 md:col-start-auto">
        {row.assignmentStatus === "offered" && (
          <>
            <Button
              size="sm"
              disabled={!actions.respond || busy || offerId === undefined}
              onClick={() => {
                if (offerId !== undefined) void onAccept(row, offerId);
              }}
            >
              Aceptar
            </Button>
            <Button
              size="sm"
              variant="secundario"
              disabled={!actions.respond || busy}
              aria-expanded={open === "reject"}
              onClick={() => setPanel(open === "reject" ? "none" : "reject")}
            >
              Rechazar
            </Button>
          </>
        )}
        {row.assignmentStatus === "accepted" && (
          <Button
            size="sm"
            disabled={!actions.assign || busy}
            aria-expanded={open === "assign"}
            onClick={() => setPanel(open === "assign" ? "none" : "assign")}
          >
            Asignar
          </Button>
        )}
      </div>
      {open === "reject" && offerId !== undefined && (
        <form
          className="col-span-full flex flex-col gap-3 rounded-md border border-linea bg-hundida p-4"
          aria-label="Rechazar la oferta"
          onSubmit={(event) => {
            event.preventDefault();
            if (reason === "") return;
            void onReject(row, offerId, reason).then((done) => {
              if (done) setPanel("none");
            });
          }}
        >
          <div className="max-w-sm">
            <SelectField
              label="Motivo del rechazo"
              hint="El viaje pasa solo al siguiente proveedor de la planta."
              value={reason}
              disabled={busy}
              onChange={(event) => setReason(event.target.value as OfferRejectReason | "")}
            >
              <option value="">Elegir motivo</option>
              {REJECT_REASONS.map((r) => (
                <option key={r} value={r}>
                  {REJECT_REASON_LABEL[r]}
                </option>
              ))}
            </SelectField>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button type="submit" size="sm" disabled={reason === "" || busy}>
              Confirmar rechazo
            </Button>
            <Button size="sm" variant="secundario" disabled={busy} onClick={() => setPanel("none")}>
              Cancelar
            </Button>
          </div>
        </form>
      )}
      {open === "assign" && (
        <div className="col-span-full">
          <AssignForm
            trip={trip}
            vehicles={vehicles}
            drivers={drivers}
            busy={busy}
            providerId={row.providerId}
            {...(lastDrivers === undefined ? {} : { lastDrivers })}
            onCancel={() => setPanel("none")}
            onSubmit={(vehicleId, driverId) => {
              void onAssign(row, vehicleId, driverId).then((done) => {
                if (done) setPanel("none");
              });
            }}
          />
        </div>
      )}
    </li>
  );
}
